import { useIndicationData } from 'features/indicationTriage'
import { useRolodexCache } from 'features/rolodex'
import { useEffect, useMemo } from 'react'
import CantileverTheme from 'styles/cantileverTheme'
import {
  GeckoMeasurementUnit,
  RolodexEntryJson,
  RolodexEntryTagsType,
  RolodexEntryType,
} from 'types'

import { CriticalProfile, CriticalProfilePoint } from './criticalProfileApi'

export function useCriticalProfileMarkups(
  profiles: CriticalProfile[],
  indicationId: string | undefined,
  colors: Record<string, string>
): string[] {
  const { component: selectedComponent } = useIndicationData()
  const tags = useMemo<RolodexEntryTagsType>(() => {
    return indicationId
      ? {
          ...selectedComponent?.tags,
          indication: indicationId,
          component: selectedComponent?.id ?? null,
        }
      : {}
  }, [indicationId, selectedComponent?.id, selectedComponent?.tags])

  const markups = useMemo<RolodexEntryJson[]>(() => {
    return profiles
      .map((profile) =>
        markupLayerFromProfile(
          profile,
          colors[profile.id] ?? CantileverTheme.colors.grayscale[700],
          tags
        )
      )
      .filter((markup) => !!markup) as RolodexEntryJson[]
  }, [colors, profiles, tags])

  const rdCache = useRolodexCache()
  useEffect(() => {
    markups.forEach((markup) => rdCache.putEntry(markup))
    return () => rdCache.clearAll()
  }, [markups, rdCache])
  return useMemo(() => markups.map((markup) => markup.id), [markups])
}

const STD_UNIT = GeckoMeasurementUnit.Millimeters

function markupLayerFromProfile(
  profile: CriticalProfile,
  color: string,
  tags: RolodexEntryTagsType
): RolodexEntryJson | null {
  if (profile.points.length < 2) {
    return null
  }
  const geometry = {
    x_measurement_unit: STD_UNIT.id,
    y_measurement_unit: STD_UNIT.id,
  }
  return {
    id: `crit-profile-markup-${profile.id}`,
    type: RolodexEntryType.MARKUP_LAYER,
    data: {
      name: profile.name,
      path: ['FFS LTA Paths'],
      customer_visible: true,
      color,
      category: 'user-added',
      geometry,
      primitives: {
        crit_prof: createLinePrimitive(profile.points),
      },
    },
    tags,
    index: {},
    generation: 1,
    updated_at: '',
    access_restriction: null,
  }
}

type MarkupLineNum = number | 'Infinity' | '-Infinity'
type MarkupLinePrimitive = {
  kind: 'line'
  points: {
    x: MarkupLineNum
    y: MarkupLineNum
  }[]
}

function createLinePrimitive(
  critPoints: ReadonlyArray<CriticalProfilePoint>
): MarkupLinePrimitive | undefined {
  if (critPoints.length < 2) {
    return undefined
  }
  return {
    kind: 'line',
    points: critPoints.map((critPoint) => ({
      x: critPoint.coords.x.convertTo(STD_UNIT).num,
      y: critPoint.coords.y.convertTo(STD_UNIT).num,
    })),
  }
}
